import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from 'antd';
import { AppleFilled } from '@ant-design/icons';
import firebase from 'firebase/app';

import { signInWithProviderFlow } from '../../utils/authFlow';

const { Text } = Typography;

/**
 * Apple Sign In button for clients.
 * Popup on desktop, redirect on mobile (authFlow) — Apple sets emailVerified=true
 * in Firebase Auth. Redirect results return via getRedirectResult in ClientLoginScreen.
 * Calls onSuccess(authResult) on completion.
 */
export default function ClientAppleLogin({ onSuccess, onError, isSignup }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new firebase.auth.OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      // Mobile browsers block popups — redirect there; popup on desktop. A null
      // result means the page is navigating away (redirect flow): the result is
      // consumed by getRedirectResult() in ClientLoginScreen after the round-trip.
      // isSignup rides along in sessionStorage so the anti-duplicate abort still
      // knows this was a deliberate signup after the redirect unload.
      const authResult = await signInWithProviderFlow(firebase.auth(), provider, { inSignupFlow: !!isSignup });
      if (authResult && mountedRef.current) onSuccess(authResult);
    } catch (err) {
      // User closed the popup — not an error worth surfacing
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        if (mountedRef.current) setLoading(false);
        return;
      }
      if (mountedRef.current) {
        setError(err.message || 'Apple sign-in failed');
        if (onError) onError(err);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <div>
      <Button
        block
        size="large"
        onClick={handleAppleSignIn}
        loading={loading}
        icon={<AppleFilled />}
        style={{
          backgroundColor: '#000',
          color: '#fff',
          borderColor: '#555',
          height: 44,
        }}
      >
        {isSignup ? 'Sign up with Apple' : 'Sign in with Apple'}
      </Button>
      {error && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
}
